import {
  List,
  TextInput,
  DatagridConfigurable,
  SelectColumnsButton,
  ExportButton,
  TopToolbar,
  FunctionField,
  ReferenceField,
  WrapperField,
  useListContext,
} from "react-admin";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { HiOutlineX } from "react-icons/hi";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { RaRowActions } from "../../components/RaRowActions";
import { AppBulkDeleteButton } from "../../components/AppBulkDeleteButton";
import { ListPageIndicator } from "../../components/TablePageIndicator";
import { determineUserReadableRole } from "../users/UserUtils";
import { colors, fontFamilyMono, radius, shadows } from "../../data/tokens";
import { ParticipatedChip } from "./user-elections-util";

// Estilo compacto de tabela — espelho do datagridSx das demais listas
const datagridSx = {
  "& .RaDatagrid-headerCell": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .RaDatagrid-rowCell": { paddingTop: 0.75, paddingBottom: 0.75 },
};

// Ano fixo na linha de filtros (era o único item do "Adicionar filtro" —
// um clique a menos). ⚠️ Hoje o servidor só aplica o ano quando o nome
// da eleição também está preenchido (condição registrada no TODO.md);
// combinado funciona, sozinho é ignorado.
const postFilters = [
  <TextInput label="Eleição" source="name" alwaysOn />,
  <TextInput label="Ano" source="year" alwaysOn />,
];

// Limpa todos os filtros ativos; só aparece quando existe filtro aplicado
const ClearAllFiltersButton = () => {
  const { filterValues, setFilters } = useListContext();
  if (!filterValues || Object.keys(filterValues).length === 0) return null;
  return (
    <Button
      size="small"
      color="secondary"
      startIcon={<HiOutlineX size={16} />}
      onClick={() => setFilters({}, [])}
    >
      Limpar filtros
    </Button>
  );
};

export const UserElectionList = () => {
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";

  const ListActions = () => (
    <TopToolbar sx={{ alignItems: "center" }}>
      <SelectColumnsButton preferenceKey="user-elections.datagrid.v2" />
      <ExportButton />
      <ClearAllFiltersButton />
      {/* navegação ancorada à direita; botões ficam à esquerda */}
      <span style={{ marginLeft: "auto", display: "flex" }}>
        <ListPageIndicator />
      </span>
    </TopToolbar>
  );

  return (
    <>
      <PageTopBreadcrumb
        title="Eleições"
        firstPath="/#/user-elections/crud"
        actionType="create"
        importButton={true}
        defaultModel="elections"
      />
      <List
        title=" "
        filters={postFilters}
        actions={<ListActions />}
        perPage={25}
        // Mantém a página atual visível enquanto a próxima carrega
        queryOptions={{ keepPreviousData: true }}
        sx={{
          // Mesmo padrão visual das listas de Sócios e Transações
          "& .RaList-main": {
            backgroundColor: colors.white,
            borderRadius: `${radius.lg}px`,
            boxShadow: shadows.card,
            padding: "12px 20px 8px",
          },
          "& .RaList-content": {
            boxShadow: "none",
            backgroundColor: "transparent",
          },
          "& .RaList-actions": {
            display: "flex",
            flexDirection: "column-reverse",
            alignItems: "stretch",
            gap: 1,
          },
          "& .RaList-actions .MuiToolbar-root": {
            position: "static",
            gap: 1,
            width: "100%",
            justifyContent: "flex-start",
            minHeight: "auto !important",
            paddingBottom: 0,
          },
          "& .RaList-actions > span:empty": { display: "none" },
          // Filtros em linha única elástica
          "& .RaList-actions form": {
            width: "100%",
            alignItems: "center",
            rowGap: 1.5,
            columnGap: 1.5,
            paddingBottom: 1,
            paddingTop: 0,
            minHeight: "auto",
          },
          "& .RaFilterForm-filterFormInput": {
            display: "flex",
            alignItems: "center",
            position: "relative",
            marginTop: "0 !important",
            marginBottom: "0 !important",
            flex: "1 1 170px",
            minWidth: 150,
            maxWidth: 320,
          },
          "& .RaFilterForm-filterFormInput .MuiFormControl-root": {
            width: "100%",
            marginTop: "0 !important",
            marginBottom: "0 !important",
          },
          // Botão ⊗ como badge no canto superior direito do campo
          "& .RaFilterFormInput-hideButton": {
            position: "absolute",
            top: -10,
            right: -10,
            zIndex: 1,
            padding: 0,
            marginTop: "0 !important",
            marginBottom: "0 !important",
            color: colors.textMuted,
            backgroundColor: colors.white,
            borderRadius: "50%",
            "& svg": { fontSize: 22 },
            "&:hover": {
              color: colors.action,
              backgroundColor: colors.white,
            },
          },
          "& .RaFilterFormInput-spacer": { display: "none" },
        }}
      >
        {/* preferenceKey versionada (v2): o menu "⋮" antigo, sem rótulo,
            deixava "Unlabeled column" nas preferências do navegador */}
        <DatagridConfigurable
          rowClick="show"
          sx={datagridSx}
          preferenceKey="user-elections.datagrid.v2"
          bulkActionButtons={<AppBulkDeleteButton />}
        >
          <FunctionField
            label="Eleição"
            sortBy="name"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: colors.primaryDark,
                }}
              >
                {record.name || "—"}
              </Typography>
            )}
          />
          <FunctionField
            label="Participou"
            sortBy="participated"
            render={(record: any) => (
              <ParticipatedChip participated={record.participated} />
            )}
          />
          <FunctionField
            label="Ano"
            sortBy="year"
            render={(record: any) => (
              <Typography
                component="span"
                sx={{ fontSize: 13, fontFamily: fontFamilyMono }}
              >
                {record.year ?? "—"}
              </Typography>
            )}
          />
          <ReferenceField
            source="userId"
            reference="users/crud"
            label="Membro"
            link="show"
          >
            <FunctionField
              render={(member: any) => (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.primaryDark,
                  }}
                >
                  {member?.fullName}
                </Typography>
              )}
            />
          </ReferenceField>
          {/* Ações visíveis (Ver sempre; Editar/Excluir só admin) no
              lugar do menu ⋮ que escondia tudo atrás de um clique */}
          <WrapperField label="Ações" textAlign="right">
            <RaRowActions show edit={isAdmin} del={isAdmin} />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    </>
  );
};
